import "./App.css";
import Home from "./components/Home";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import Calendar from "./components/Calendar";
import AutomaticResponseHistory from "./components/AutomaticResponseHistory";
import VacantConfirmation from "./components/VacantConfirmation";
import NonVacantConfirmation from "./components/NonVacantConfirmation";
import { GoogleOAuthProvider } from "@react-oauth/google";
import CancelConfirmation from "./components/CancelConfirmation";
import { useEffect, useState } from "react";
import Navbar from "./components/Navbar";
import Login from "./components/Login";
import UnprocessedEmails from "./components/UnprocessedEmails";
import DashBoard from "./components/DashBoard";
import EmailTemplates from "./components/EmailTemplates";
import CancelMainAppointment from "./components/CancelMainAppointment";
import AppointmentScheduleAndConfirmation from "./components/AppointmentScheduleAndConfirmation";
import MemberAndGurantorPage from "./components/MemberAndGurantorPage";
import MembersDetails from "./components/MembersDetails";
import AppfolioDashboard from "./components/AppfolioDashboard";
import RentedListing from "./components/RentedListing";
import SlotConfirmation from "./components/SlotConfirmation";
import SecurityDepositReceiverSelection from "./components/SecurityDepositReceiverSelection";
import KpisPage from "./components/KpisPage";
import BankDetailForm from "./components/BankDetailForm";
import EmailInbox from "./components/EmailInbox";
import Users from "./components/Users";
import ProtectedRoute from "./components/ProtectedRoute";
import { getTokenFromLocalStorage } from "./components/DataServices/Configuration";
import AssistantPdfUpload from "./components/AssistantPdfUpload";
import NotesPage from "./components/NotesPage";
import ExtractedApartments from "./components/ExtractedApartments";
import ExtractedApartmentDetail from "./components/ExtractedApartmentDetail";
import RenewalDashboard from "./components/RenewalDashboard";
import RenewalEmailInbox from "./components/RenewalEmailInbox";
import PropertyTimeline from "./components/PropertyTimeline";

interface IListing {
  id: string;
  idApartment: string;
  title: string;
  description: string;
  rent: string;
  moveInDate: string;
  showings: string;
  areAvailableAppointmentsSet: boolean;
  isSelected: boolean;
  isVacant: boolean;
  building: string;
  line: string;
  floor: number;
  numOfBedrooms: number;
  currentRent: number;
}

function App() {
  const [token, setToken] = useState<string | null>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Validate token on app load
    const validateToken = async () => {
      try {
        const validToken = await getTokenFromLocalStorage();
        if (validToken) {
          setToken(validToken);
        } else {
          setToken(null);
          // Clear any invalid tokens
          localStorage.removeItem("token");
          localStorage.removeItem("expires_at");
        }
      } catch (error) {
        console.error("Token validation failed:", error);
        setToken(null);
        // Clear any invalid tokens
        localStorage.removeItem("token");
        localStorage.removeItem("expires_at");
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, []);

  const relatedListings: IListing[] = [
    {
      id: "abcdef12-3456-7890-abcd-1234567890ab",
      idApartment: "abcdef12-0000-7890-abcd-1234567890ab",
      title: "",
      description: "",
      rent: "",
      moveInDate: "",
      showings: "",
      areAvailableAppointmentsSet: true,
      isSelected: false,
      isVacant: false,
      building: "",
      line: "",
      floor: 0,
      numOfBedrooms: 0,
      currentRent: 0.0,
    },
    // Add more appointment objects as needed
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <GoogleOAuthProvider clientId="1071259456917-bv12vdcuc6ccv0i7qogkhfa8548b8t48.apps.googleusercontent.com">
        <Router>
          <Routes>
            {/* Root */}
            <Route
              index
              path="/"
              element={
                token ? (
                  <Navigate to="/home" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* Login is ALWAYS available */}
            <Route path="/login" element={<Login setToken={setToken} />} />

            {/* ---------------- PROTECTED ROUTES ---------------- */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <DashBoard />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/listing"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <Home />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/rented-listing"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <RentedListing />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <Calendar
                      relatedListing={relatedListings}
                      showAvailableAppointments={false}
                    />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/AutomaticResponseHistory"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <AutomaticResponseHistory />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/members-details"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <MembersDetails />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute requirePrimaryUser={true}>
                  <Navbar setToken={setToken}>
                    <Users />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/UnprocessedEmails"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <UnprocessedEmails />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/appfolioDashboard"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <AppfolioDashboard />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/emails/:apartmentId"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <EmailTemplates />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/kpis/:listingId"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <KpisPage />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/assistant-settings"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <AssistantPdfUpload />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/notes"
              element={
                <ProtectedRoute>
                  <NotesPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/extracted-apartments"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <ExtractedApartments />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/extracted-apartments/:id"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <ExtractedApartmentDetail />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/renewal"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <RenewalDashboard />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/timeline/:unitId?"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <PropertyTimeline />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/timeline/by-listing/:listingId"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <PropertyTimeline />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            <Route
              path="/renewal-inbox/:renewalId"
              element={
                <ProtectedRoute>
                  <Navbar setToken={setToken}>
                    <RenewalEmailInbox />
                  </Navbar>
                </ProtectedRoute>
              }
            />

            {/* ---------------- PUBLIC ROUTES (UNCHANGED) ---------------- */}
            <Route
              path="/bank-detail-form/:listingId/:receiverId"
              element={<BankDetailForm />}
            />
            <Route
              path="/security-deposit-receiver/:listingId/:personId"
              element={<SecurityDepositReceiverSelection />}
            />
            <Route
              path="/addTenantsAndGuarantorInfo/:id"
              element={<MemberAndGurantorPage />}
            />
            <Route
              path="/vacant-confirmation/:id"
              element={<AppointmentScheduleAndConfirmation type="" />}
            />
            <Route
              path="/nonvacant-confirmation/:id"
              element={<AppointmentScheduleAndConfirmation type="" />}
            />
            <Route
              path="/cancel-confirmation/:id"
              element={<CancelConfirmation />}
            />
            <Route
              path="/reschedule-confirmation/:id"
              element={<AppointmentScheduleAndConfirmation type="reschedule" />}
            />
            <Route
              path="/rescheduleConfirmation/:id"
              element={
                <AppointmentScheduleAndConfirmation type="rescheduleByAdmin" />
              }
            />
            <Route
              path="/Cancel-mainAppointment/:mainAppointmentId"
              element={<CancelMainAppointment />}
            />
            <Route
              path="/slotconfirmation/:listingId/:personId"
              element={<SlotConfirmation />}
            />
            <Route
              path="/inbox/:listingId/:apartmentName?"
              element={<EmailInbox />}
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </GoogleOAuthProvider>
    </>
  );
}

export default App;
